import React, { useState } from "react";
import { capitalize } from "../../utils/spec-utils.js";
import {
  PrimaryButton,
  SecondaryButton,
  TextButton,
  IconOrder,
  IconSales,
  withTooltip,
  TOOLTIP_HORIZONTAL_ALIGNMENTS,
  TOOLTIP_VERTICAL_ALIGNMENTS,
  THEMES,
  Dialog,
  Radio,
  Divider,
  Toast,
  TOAST_POSITIONS,
  TOAST_VARIANTS
} from "cdk-radial";
import {
  API_TYPES_DROPDOWN_OPTIONS,
  RESTRICTED_SHARED_STATUS
} from "../api-directory-home/constants.js";
import {
  API_DIRECTORY_THEME,
  API_VISIBILITY,
  CONTRACT_TEXT,
  BUNDLE_TYPE
} from "../../utils/constants.js";
import {
  AMPLITUDE_EVENTS,
  AMPLITUDE_PROPERTIES
} from "../../utils/AmplitudeConstants.js";
import "./ApiDetails.css";
import { BUILD_WITH_API } from "../api-directory-home/constants.js";
import { PrivateIcon } from "../common/icons/icons.js";
import { useDirectoryType } from "../../contexts/DirectoryTypeContext";

const BTN_TEXT = {
  ADD_TO_CART: "Add to Cart",
  ADDED_TO_CART: "Added to Cart",
  VIEW_IN_MY_APIS: "View in My APIs",
  REQUESTED_ACCESS: "Requested Info",
  REQUEST_ACCESS: "Request Info"
};

const ContractIcon = ({ isBundleContracted }) => {
  const IconWithTooltip = withTooltip(
    props => <IconSales {...props} className="api-dir-pkg_icons" />,
    {
      id: "contract-icon",
      text: CONTRACT_TEXT,
      theme: THEMES.LIGHT
    }
  );

  return (
    <IconWithTooltip
      horizontalAlignment={TOOLTIP_HORIZONTAL_ALIGNMENTS.LEFT}
      verticalAlignment={TOOLTIP_VERTICAL_ALIGNMENTS.BOTTOM}
    />
  );
};

const getAvailableApis = updatedBundleDetails => ({
  ...updatedBundleDetails,
  apis: updatedBundleDetails?.apis?.filter(
    api => !api.isInCart && !api.inOrder && !api.isContracted
  )
});

const formatBundleDetails = (bundleDetails, isPriceBundle) => ({
  bundleId: bundleDetails?.id,
  bundleType: isPriceBundle ? BUNDLE_TYPE.PRICE : BUNDLE_TYPE.GENERAL,
  cart: bundleDetails?.apis?.map(api => ({
    resourceId: api.id,
    resourceType: api.type
  }))
});

const ApiTitle = ({
  bundleId,
  bundlename,
  bundleVisibility,
  isBundleContracted,
  isPriceBundle,
  bundleDetailsViewEnabled,
  apiId,
  apiType,
  apiName,
  domainDisplayNames,
  tags,
  publishedBy,
  asyncApi,
  orgLogo,
  apiDetailsOnClickHandlers,
  cartDetails,
  orderDetails,
  sendAmplitudeData,
  visibility,
  isContracted,
  isRestricted,
  restrictedSharedStatus,
  existingSolutionDetails,
  disableBuildNow,
  disableAddBundleToCart,
  isBundleInOrder,
  updatedBundleDetails,
  apiHomeOnClickHandlers,
  addingAPIToCart,
  isAPIContracted,
  isMsaConsented,
  skipApiPricing,
  orgId,
  requestingOrg,
  loadingRestrictedAccess,
  isCurrentApiInBundleCart
}) => {
  const belongsTo = useDirectoryType();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedRadio, setSelectedRadio] = useState([]);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [isRequested, setIsRequested] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const urlParams = new URLSearchParams();
  const apiTypeparams = asyncApi ? "asyncApi" : "restApi";

  // Derived state
  const showContractIcon = isContracted;
  const categories = domainDisplayNames
    ?.filter(approvedTag => tags?.includes(approvedTag?.id))
    .map(tag => ({ id: tag.id, name: tag.name }));
  const isAPIInCart = cartDetails?.some(
    api => api.resourceId === apiId || api.resourceId === bundleId
  );

  const allOrderedAPIs = [
    ...(orderDetails?.restApis || []),
    ...(orderDetails?.asyncApis || [])
  ];
  const isAPIInOrder = allOrderedAPIs?.some(api => api.id === apiId);
  const availableBundleApis = getAvailableApis(updatedBundleDetails);
  const bundlePayload = formatBundleDetails(availableBundleApis, isPriceBundle);
  const ownOrgApi = requestingOrg === orgId;

  const shouldSkipRestriction = isContracted || ownOrgApi || skipApiPricing;

  const isEligibleForRestriction =
    isRestricted &&
    !shouldSkipRestriction &&
    [
      RESTRICTED_SHARED_STATUS.NOT_REQUESTED,
      RESTRICTED_SHARED_STATUS.REQUESTED
    ].includes(restrictedSharedStatus);

  // Event handlers
  const handleBuildWithApi = () => {
    if (existingSolutionDetails.length === 0) {
      apiDetailsOnClickHandlers.buildWithApi({
        appId: selectedRadio?.id,
        buildWithType: BUILD_WITH_API.NEW_APP,
        apiId
      });
    } else {
      setIsDialogOpen(true);
    }
  };

  const handleBuildWithBundle = () => {
    if (existingSolutionDetails.length === 0) {
      apiDetailsOnClickHandlers.buildWithBundle({
        appId: selectedRadio?.id,
        buildWithType: BUILD_WITH_API.NEW_APP,
        bundleId
      });
    } else {
      setIsDialogOpen(true);
    }
  };

  const removeToast = () => {
    setShowSuccessToast(false);
  };

  const buildWithSelectedApp = () => {
    const buildType =
      selectedRadio === BUILD_WITH_API.NEW_APP
        ? BUILD_WITH_API.NEW_APP
        : BUILD_WITH_API.EDIT_APP;
    if (bundleDetailsViewEnabled) {
      apiDetailsOnClickHandlers.buildWithBundle({
        appId: selectedRadio?.id,
        buildWithType: buildType,
        bundleId
      });
    } else {
      apiDetailsOnClickHandlers.buildWithApi({
        appId: selectedRadio?.id,
        buildWithType: buildType,
        apiId
      });
    }
    setIsDialogOpen(false);
  };

  const handleApiTypeFilter = () => {
    urlParams.set("Type", apiTypeparams);
    sessionStorage.setItem("selectedFilters", urlParams.toString());
    const newUrl = `/?${urlParams.toString()}`;

    apiDetailsOnClickHandlers.apiTypeFilter({
      newUrl,
      selectedAPIType: [
        asyncApi
          ? API_TYPES_DROPDOWN_OPTIONS[1].value
          : API_TYPES_DROPDOWN_OPTIONS[0].value
      ],
      selectedDomainIds: []
    });

    sendAmplitudeData(AMPLITUDE_EVENTS.SELECTED_API_TYPE, {
      [AMPLITUDE_PROPERTIES.SELECTED_API_TYPE]: {
        [AMPLITUDE_PROPERTIES.SELECTED_API_TYPE]: asyncApi
          ? "ASYNC API"
          : "REST API" || ""
      }
    });
  };

  const handleApiCategoryFilter = category => {
    if (category?.name) {
      urlParams.set("Category", category.id);
    }
    sessionStorage.setItem("selectedFilters", urlParams.toString());
    const newUrl = `/?${urlParams.toString()}`;

    apiDetailsOnClickHandlers.apiCategoryFilter({
      selectedAPIType: [],
      selectedDomainIds: [category.id],
      newUrl
    });

    sendAmplitudeData(AMPLITUDE_EVENTS.SELECTED_CATEGORY_TYPE, {
      [AMPLITUDE_PROPERTIES.SELECTED_CATEGORY]: {
        [AMPLITUDE_PROPERTIES.SELECTED_CATEGORY]: category.name || ""
      }
    });
  };

  const getAPIActionButton = () => {
    if (isEligibleForRestriction) {
      const restrictedApiNotRequested =
        restrictedSharedStatus === RESTRICTED_SHARED_STATUS.NOT_REQUESTED;

      return (
        <PrimaryButton
          text={
            restrictedApiNotRequested
              ? BTN_TEXT.REQUEST_ACCESS
              : BTN_TEXT.REQUESTED_ACCESS
          }
          className={
            restrictedApiNotRequested
              ? "api-dir-pkg_request-btn"
              : "api-dir-pkg_requested-btn"
          }
          isDisabled={!restrictedApiNotRequested}
          isLoading={loadingRestrictedAccess}
          onClick={event => {
            if (event.target.innerText === BTN_TEXT.REQUEST_ACCESS) {
              apiDetailsOnClickHandlers.requestRestrictedAccess({
                apiId,
                apiType,
                btn: BTN_TEXT.REQUEST_ACCESS
              });
            }
            event.stopPropagation();
          }}
        />
      );
    }

    if (isAPIInOrder) {
      return (
        <TextButton
          dataTestId="added-to-cart-button"
          iconSize={24}
          size="standard"
          text={BTN_TEXT.VIEW_IN_MY_APIS}
          onClick={() =>
            apiDetailsOnClickHandlers.viewInMyAPIs({
              apiId,
              apiType,
              btn: BTN_TEXT.VIEW_IN_MY_APIS
            })
          }
        />
      );
    }

    if ((isAPIInCart || isCurrentApiInBundleCart) && !isAPIContracted) {
      return (
        <TextButton
          dataTestId="added-to-cart-button"
          className="api-dir-pkg_added-to-cart-btn"
          iconSize={24}
          isDisabled
          size="standard"
          text={BTN_TEXT.ADDED_TO_CART}
          style={{ cursor: "auto" }}
        />
      );
    }

    if (!isAPIContracted) {
      return (
        <PrimaryButton
          iconColor="primary"
          text={BTN_TEXT.ADD_TO_CART}
          className="api-dir-pkg_add-to-cart-btn"
          onClick={() =>
            apiDetailsOnClickHandlers.addToCart({
              resourceId: apiId,
              resourceType: apiType,
              btn: BTN_TEXT.ADD_TO_CART
            })
          }
          isLoading={addingAPIToCart}
          isDisabled={!isMsaConsented}
        />
      );
    }

    return null;
  };

  const renderBundleActionButtonForISV = () => {
    if (belongsTo === API_DIRECTORY_THEME.ISV_API_DIRECTORY) {
      return (
        <PrimaryButton
          iconColor="primary"
          text="Build with this Bundle"
          onClick={handleBuildWithBundle}
          isDisabled={disableBuildNow}
        />
      );
    }

    return null;
  };

  const renderBundleActionButton = () => {
    if (isBundleInOrder) {
      return (
        <TextButton
          dataTestId="added-to-cart-button"
          iconSize={24}
          size="standard"
          text={BTN_TEXT.VIEW_IN_MY_APIS}
          onClick={() => apiHomeOnClickHandlers.viewInMyAPIs()}
        />
      );
    }

    if ((disableAddBundleToCart || isAPIInCart) && !isBundleContracted) {
      return (
        <TextButton
          dataTestId="added-to-cart-button"
          className="api-dir-pkg_added-to-cart-btn"
          iconSize={24}
          isDisabled
          size="standard"
          text={BTN_TEXT.ADDED_TO_CART}
          style={{ cursor: "auto" }}
        />
      );
    }

    if (!isBundleContracted) {
      return (
        <PrimaryButton
          iconColor="primary"
          text={BTN_TEXT.ADD_TO_CART}
          className="api-dir-pkg_add-to-cart-btn"
          isLoading={addingAPIToCart}
          onClick={() =>
            apiHomeOnClickHandlers.addBundleIntoCart(bundlePayload)
          }
          isDisabled={!isMsaConsented}
        />
      );
    }

    return null;
  };

  const renderBuildDialog = () => (
    <Dialog
      buttonsProps={[
        {
          id: "dialog-action-1",
          onClick: buildWithSelectedApp,
          text: "Continue",
          variant: "text",
          isDisabled: selectedRadio.length === 0
        }
      ]}
      dataTestId="existing-solutions-sbdialog"
      isOpen={isDialogOpen}
      id="sbdialog"
      onClose={() => {
        setIsDialogOpen(false);
        setSelectedRadio([]);
      }}
      title="Build Now"
    >
      <div className="api-dir-pkg_dialog_bundle_card">
        <div style={{ marginLeft: "-16px" }}>
          <Radio
            dataTestId="radioNewApp"
            id="radioNewApp"
            label={
              bundleDetailsViewEnabled ? (
                <span>
                  Build a new App with <strong>{bundlename}</strong> - Bundle
                </span>
              ) : (
                <span>
                  Build a new App with <strong>{apiName}</strong>
                </span>
              )
            }
            labelPosition="after"
            checked={selectedRadio === BUILD_WITH_API.NEW_APP}
            name="radio"
            onChange={() => setSelectedRadio(BUILD_WITH_API.NEW_APP)}
            size="standard"
            value="newApp"
          />
        </div>
        {existingSolutionDetails?.length > 0 && (
          <>
            <Divider />
            <div className="api-dir-pkg_select_existing_apps">
              Select an existing App to add the{" "}
              {bundleDetailsViewEnabled ? (
                <>
                  <b>{bundlename}</b> - Bundle
                </>
              ) : (
                <b>{apiName}</b>
              )}
            </div>
            {existingSolutionDetails?.map((app, index) => (
              <div
                key={index}
                className="api-dir-pkg_radio_app_names_list"
                style={{ marginLeft: "-16px" }}
              >
                <Radio
                  dataTestId={`radio-${index}`}
                  id={`radio-${index}`}
                  label={
                    <div>
                      <strong>{app.displayName}</strong>
                    </div>
                  }
                  labelPosition="after"
                  name="radio"
                  checked={selectedRadio === app}
                  onChange={() => setSelectedRadio(app)}
                  size="standard"
                  value={app}
                />
              </div>
            ))}
          </>
        )}
      </div>
    </Dialog>
  );

  return (
    <div className="api-dir-pkg_header-container">
      <div className="api-dir-pkg_text-container">
        <div className="api-dir-pkg_api-details">
          <div className="api-dir-pkg_api-name">
            {bundleDetailsViewEnabled ? (
              <>
                <span className="api-dir-pkg_bundle-name">{bundlename}</span>
                <span className="api-dir-pkg_bundle-text"> - Bundle</span>
              </>
            ) : (
              apiName && capitalize(apiName)
            )}
            {belongsTo === API_DIRECTORY_THEME.DEALER_API_DIRECTORY &&
              bundleVisibility === API_VISIBILITY.PRIVATE &&
              bundleDetailsViewEnabled && (
                <span
                  style={{ marginLeft: "8px" }}
                  data-testid="icon-private-api"
                >
                  <PrivateIcon />
                </span>
              )}
            {visibility === API_VISIBILITY.PRIVATE ? (
              <span
                style={{ display: "inline-block" }}
                data-testid="icon-private-api"
              >
                <PrivateIcon />
              </span>
            ) : null}
            {showContractIcon && (
              <span className="api-dir-pkg_api-contracted-icon">
                <ContractIcon />
              </span>
            )}
            {belongsTo === API_DIRECTORY_THEME.DEALER_API_DIRECTORY &&
              isBundleContracted &&
              bundleDetailsViewEnabled && (
                <span className="api-dir-pkg_api-contracted-icon">
                  <ContractIcon isBundleContracted={isBundleContracted} />
                </span>
              )}
          </div>
          {!bundleDetailsViewEnabled && (
            <div className="api-dir-pkg_api-filters-container">
              <span className="api-dir-pkg_key">API Type: </span>
              <span
                className="api-dir-pkg_value api-dir-pkg_link"
                onClick={handleApiTypeFilter}
              >
                {asyncApi ? "ASYNC API" : "REST API"}
              </span>
              {publishedBy && (
                <>
                  <span className="api-dir-pkg_separator">|</span>
                  <span className="api-dir-pkg_key">Publisher: </span>
                  <span className="api-dir-pkg_value">{publishedBy}</span>
                </>
              )}
              {categories?.length > 0 && (
                <>
                  <span className="api-dir-pkg_separator">|</span>
                  <span className="api-dir-pkg_key">Categories: </span>
                  {categories.map((category, idx) => (
                    <span
                      key={idx}
                      className="api-dir-pkg_value api-dir-pkg_link"
                      data-cy="api-domain"
                      onClick={() => handleApiCategoryFilter(category)}
                    >
                      {category.name}
                      {categories[categories.length - 1].id !== category.id &&
                        ", "}
                    </span>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="api-dir-pkg_action-btn-container">
        {bundleDetailsViewEnabled &&
          belongsTo === API_DIRECTORY_THEME.DEALER_API_DIRECTORY &&
          renderBundleActionButton()}
        {bundleDetailsViewEnabled &&
          belongsTo === API_DIRECTORY_THEME.ISV_API_DIRECTORY &&
          renderBundleActionButtonForISV()}
        {!bundleDetailsViewEnabled &&
          belongsTo === API_DIRECTORY_THEME.ISV_API_DIRECTORY && (
            <>
              {isContracted || ownOrgApi || skipApiPricing ? (
                <PrimaryButton
                  iconColor="primary"
                  text="Build with this API"
                  onClick={handleBuildWithApi}
                  isDisabled={disableBuildNow}
                />
              ) : isRestricted ? (
                restrictedSharedStatus === "active" ? (
                  <PrimaryButton
                    iconColor="primary"
                    text="Build with this API"
                    onClick={handleBuildWithApi}
                    isDisabled={disableBuildNow}
                  />
                ) : restrictedSharedStatus === "requested" ? (
                  <PrimaryButton
                    iconColor="primary"
                    text="Requested Info"
                    isDisabled={true}
                  />
                ) : (
                  <PrimaryButton
                    iconColor="primary"
                    text={
                      isRequested || isRequesting
                        ? "Requested Info"
                        : "Request Info"
                    }
                    onClick={() =>
                      apiDetailsOnClickHandlers.handleRequestAccess({
                        setIsRequesting,
                        setIsRequested,
                        setShowSuccessToast
                      })
                    }
                    isLoading={isRequesting}
                    isDisabled={isRequesting || isRequested}
                  />
                )
              ) : (
                <PrimaryButton
                  iconColor="primary"
                  text="Build with this API"
                  onClick={handleBuildWithApi}
                  isDisabled={disableBuildNow}
                />
              )}
            </>
          )}
        {!bundleDetailsViewEnabled &&
          belongsTo === API_DIRECTORY_THEME.DEALER_API_DIRECTORY &&
          getAPIActionButton()}

        {renderBuildDialog()}

        {belongsTo === API_DIRECTORY_THEME.DEALER_API_DIRECTORY && (
          <SecondaryButton
            icon={<IconOrder className="api-dir-pkg_icon" />}
            className={`api-dir-pkg_my-cart-btn ${
              !isMsaConsented ? "no-border" : ""
            }`}
            text={`My Cart${
              cartDetails?.length ? ` (${cartDetails.length})` : ""
            }`}
            onClick={() => apiDetailsOnClickHandlers.openMyCart()}
            isDisabled={!isMsaConsented}
          />
        )}
      </div>
      {showSuccessToast && (
        <Toast
          actions={[
            {
              onClick: removeToast,
              text: "Dismiss"
            }
          ]}
          position={TOAST_POSITIONS.FIXED}
          variant={TOAST_VARIANTS.POSITIVE}
          onVisibilityDurationEnd={removeToast}
          id="bundle-create-toast"
          content="Your request has been submitted and you will be updated via email."
          style={{
            zIndex: 1000,
            Height: "88px",
            minWidth: "497px",
            marginLeft: "929px"
          }}
        />
      )}
    </div>
  );
};

export { ApiTitle };
